import { criterion, fail, fromCriteria, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { readSlideMasters } from "@/office/powerpoint/design";
import { readPresentation } from "@/office/powerpoint/presentation";
import { nichtPruefbar } from "../schnittstelle";

export const lektionVorlagen: Lesson = {
  id: "vorlagen",
  title: "Präsentationsvorlagen (.potx)",
  summary: "Einmal aufsetzen, immer wieder verwenden – und das Original bleibt unangetastet.",
  durationMinutes: 10,
  learningGoals: [
    "Den Unterschied zwischen Präsentation, Vorlage und Design erklären",
    "Eine eigene Vorlage speichern und verwenden",
    "Die Layouts einer Vorlage vollständig aufbauen",
  ],
  steps: [
    {
      id: "drei-dateiarten",
      title: "Drei Dateiarten, drei Zwecke",
      explanation: [
        "PowerPoint kennt drei Dateiarten, die leicht verwechselt werden. Eine " +
          "Präsentation (.pptx) ist das fertige Dokument. Ein Design (.thmx) enthält nur " +
          "Farben, Schriften und Effekte und lässt sich auf jede beliebige Präsentation " +
          "anwenden – auch auf eine bestehende. Eine Vorlage (.potx) enthält alles: " +
          "Design, Folienmaster mit allen Layouts, und zusätzlich Beispielfolien und " +
          "Platzhaltertexte.",
        "Der Unterschied im Verhalten macht die Vorlage aus: Ein Doppelklick auf eine " +
          ".potx-Datei öffnet nicht die Vorlage, sondern eine unbenannte Kopie davon. Das " +
          "Original kann gar nicht versehentlich überschrieben werden. Genau wie bei der " +
          "Dokumentvorlage im Word-Kurs.",
        "Gespeichert wird über „Datei“ → „Speichern unter“ mit dem Dateityp " +
          "„PowerPoint-Vorlage (*.potx)“. PowerPoint schlägt dabei automatisch den Ordner " +
          "„Benutzerdefinierte Office-Vorlagen“ vor. Wer diesen Vorschlag annimmt, findet " +
          "die Vorlage danach unter „Datei“ → „Neu“ → „Persönlich“.",
        "Wann was: Für ein Team, das schon mit vielen Präsentationen arbeitet, ist ein " +
          "Design praktischer – es lässt sich nachträglich anwenden. Für neue " +
          "Präsentationen, die einen definierten Aufbau haben sollen, ist die Vorlage " +
          "richtig, weil sie auch Beispielfolien mitbringt.",
      ],
      keyPoints: [
        "Speichern unter → Dateityp „PowerPoint-Vorlage (*.potx)“.",
        "Ein Doppelklick auf eine Vorlage öffnet immer eine neue Kopie.",
        "Design = nur Gestaltung, Vorlage = Gestaltung plus Startinhalte.",
      ],
    },
    {
      id: "layouts-vollstaendig",
      title: "Welche Layouts eine Vorlage braucht",
      explanation: [
        "Eine Vorlage ist so gut wie ihre Layouts. Fehlt für einen häufigen Folientyp " +
          "das passende Layout, greifen die Nutzenden zu „Leer“ und ziehen Textfelder auf " +
          "– und der ganze Aufwand war umsonst.",
        "Vier Layouts decken den Alltag ab: „Titelfolie“ für den Einstieg, „Titel und " +
          "Inhalt“ als Arbeitspferd, „Abschnittsüberschrift“ für die Trenner und „Zwei " +
          "Inhalte“ für Gegenüberstellungen. Wer eine Vorlage baut, sollte diese vier " +
          "auf jeden Fall pflegen.",
        "Darüber hinaus lohnen sich meist zwei weitere: eine Bildfolie mit großem " +
          "Bildplatzhalter und eine Schlussfolie mit Kontaktangaben. Beide werden häufig " +
          "gebraucht und sonst regelmäßig von Hand gebaut.",
        "Eigene Layouts entstehen in der Masteransicht: „Ansicht“ → „Folienmaster“ → " +
          "„Layout einfügen“, dann über „Platzhalter einfügen“ die gewünschten Bereiche " +
          "setzen und das Layout per Rechtsklick umbenennen. Der Name erscheint danach im " +
          "Layoutkatalog – ein sprechender Name ist deshalb keine Kosmetik.",
      ],
      keyPoints: [
        "Ansicht → Folienmaster → Layout einfügen → Platzhalter einfügen.",
        "Layouts per Rechtsklick umbenennen; der Name steht im Layoutkatalog.",
        "Vier Pflichtlayouts: Titelfolie, Titel und Inhalt, Abschnittsüberschrift, Zwei Inhalte.",
      ],
      task: {
        instruction:
          "Prüfe, dass der Folienmaster deiner Präsentation die vier wichtigsten Layouts " +
          "mitbringt: Titelfolie, ein Inhaltslayout, Abschnittsüberschrift und ein " +
          "Layout mit zwei Inhalten.",
        hint: "Ansicht → Folienmaster; die Layouts stehen links unter dem Master.",
        check: async () => {
          const master = await readSlideMasters();

          if (master === null || master.length === 0) {
            return nichtPruefbar(
              "Die Layouts des Folienmasters",
              "1.3",
              "Öffne „Ansicht“ → „Folienmaster“ und sieh die Layouts links durch.",
            );
          }

          const layouts = master.flatMap((eintrag) => eintrag.layouts);
          const mitTyp = layouts.some((layout) => layout.type !== null);

          // Sprachunabhängig über den Layouttyp, sonst ersatzweise über den Namen.
          const hat = (typen: string[], muster: RegExp) =>
            mitTyp
              ? layouts.some((layout) => layout.type !== null && typen.includes(layout.type))
              : layouts.some((layout) => muster.test(layout.name));

          return fromCriteria(
            [
              criterion("Layout „Titelfolie“", hat(["Title"], /titelfolie|title slide/i)),
              criterion(
                "Ein Inhaltslayout („Titel und Inhalt“)",
                hat(["Object", "TextAndObject", "Text"], /titel und inhalt|title and content/i),
              ),
              criterion(
                "Layout „Abschnittsüberschrift“",
                hat(["SectionHeader"], /abschnitt|section/i),
              ),
              criterion(
                "Ein Layout mit zwei Inhalten",
                hat(["TwoObjects", "Comparison", "TwoColumnText"], /zwei inhalte|two content|vergleich/i),
              ),
            ],
            {
              success: `Der Folienmaster („${master[0].name || "unbenannt"}“) bringt ${layouts.length} Layouts mit und deckt alle vier Grundfälle ab.`,
              failure: mitTyp
                ? "Im Folienmaster fehlen noch Layouts:"
                : "Im Folienmaster fehlen noch Layouts. Diese PowerPoint-Version liefert nur die Layoutnamen, " +
                  "gesucht wurde deshalb nach den deutschen und englischen Standardnamen – bei umbenannten Layouts kann die Prüfung danebenliegen:",
              hint: "Ansicht → Folienmaster → „Layout einfügen“, Platzhalter setzen und das Layout sprechend benennen.",
            },
          );
        },
      },
    },
    {
      id: "was-hineingehoert",
      title: "Was sonst in eine Vorlage gehört",
      explanation: [
        "Über die Layouts hinaus lohnt es sich, einer Vorlage ein paar Dinge " +
          "mitzugeben, die den Nutzenden Arbeit abnehmen.",
        "Erstens Beispielfolien: eine ausgefüllte Titelfolie, eine Inhaltsfolie mit " +
          "einer Musteraufzählung, eine Trennfolie, eine Schlussfolie. Sie zeigen, wie " +
          "die Vorlage gemeint ist, und werden beim Erstellen der eigenen Präsentation " +
          "überschrieben.",
        "Zweitens gefüllte Platzhaltertexte in den Layouts. Statt „Text durch Klicken " +
          "hinzufügen“ kann dort „Ihre Kernaussage in einem Satz“ stehen. Das gibt einen " +
          "Hinweis auf die gewünschte Verwendung, ohne dass jemand eine Anleitung lesen " +
          "muss.",
        "Drittens Fußzeile und Foliennummer, im Master fertig eingerichtet und " +
          "positioniert. Und viertens – oft vergessen – die Foliengröße: Wenn im " +
          "Unternehmen 16:9 gilt, sollte die Vorlage das mitbringen.",
        "Was nicht hineingehört: echte Inhalte aus einer bestimmten Präsentation. Eine " +
          "Vorlage, in der noch die Zahlen des letzten Quartals stehen, wird irgendwann " +
          "versehentlich mit versendet.",
      ],
      keyPoints: [
        "Beispielfolien zeigen die gedachte Verwendung.",
        "Platzhaltertexte in den Layouts können konkrete Hinweise geben.",
        "Fußzeile, Foliennummer und Foliengröße gehören fertig eingerichtet dazu.",
      ],
      task: {
        instruction:
          "Blende in deiner Präsentation eine Fußzeile ein und trag dort etwas ein – zum " +
          "Beispiel den Namen der Veranstaltung oder deiner Organisation.",
        hint: "Einfügen → Kopf- und Fußzeile → Häkchen bei „Fußzeile“, Text eintragen, „Für alle übernehmen“.",
        check: async () => {
          const snapshot = await readPresentation();

          if (!snapshot.features.placeholders) {
            return nichtPruefbar(
              "Die Platzhalterart einer Form",
              "1.8",
              "Sieh selbst nach, ob unten auf deinen Folien eine Fußzeile steht.",
            );
          }
          if (snapshot.slides.length === 0) {
            return fail(
              "In der Präsentation ist noch keine Folie vorhanden.",
              "Lege zuerst Folien an.",
            );
          }

          const fusszeilen = snapshot.slides.flatMap((folie) =>
            folie.shapes
              .filter((form) => form.placeholder?.type === "Footer")
              .map((form) => ({ nummer: folie.index + 1, text: form.paragraphs.join(" ") })),
          );

          if (fusszeilen.length === 0) {
            return fail(
              "Auf keiner Folie ist ein Fußzeilenplatzhalter eingeblendet.",
              "Einfügen → Kopf- und Fußzeile → Häkchen bei „Fußzeile“ setzen, Text eintragen und „Für alle übernehmen“ drücken.",
            );
          }

          const gefuellt = fusszeilen.filter((eintrag) => eintrag.text.trim().length > 0);
          if (gefuellt.length === 0) {
            return fail(
              `Die Fußzeile ist auf ${fusszeilen.length} Folien eingeblendet, aber leer.`,
              "Im Dialog „Kopf- und Fußzeile“ in das Feld unter „Fußzeile“ einen Text eintragen.",
            );
          }

          return pass(
            `Die Fußzeile steht auf ${gefuellt.length} Folien und trägt den Text „${gefuellt[0].text.slice(0, 40)}“.`,
          );
        },
      },
    },
  ],
};
